import { api } from "@/api/client";

/**
 * Оборачиваемость запасов по категориям номенклатуры — живьём из 1С.
 *
 * Типы повторяют `backend/src/wh_helper/warehouse_turnover/schemas.py` 1:1.
 * Все формулы (средний остаток, дни, «нет движения») считает бэкенд; фронт
 * рисует пришедшее и ничего не пересчитывает. Категория — в ДЕНЬГАХ,
 * позиция — в ШТУКАХ.
 */

/** Позиция = номенклатура. Нет в справочнике — в `name` стоит GUID, причина в
 *  `warnings` ответа. */
export interface TurnoverItem {
  nomenclature_ref: string;
  article: string;
  name: string;
  expense_qty: number;
  expense_sum: number;
  /** Ключ документа расхода (assembly/sale/ptmo/kitting) → штуки. */
  expense_by_doc_qty: Record<string, number>;
  avg_stock_qty: number;
  /** Средний остаток в деньгах — по тем же срезам, что и штуки. */
  avg_stock_sum: number;
  stock_qty: number;
  stock_sum: number;
  /** Средний остаток / среднедневной расход; `null`, когда расхода нет. */
  turnover_days: number | null;
  /** Текущий остаток / среднедневной расход; `null`, когда расхода нет. */
  cover_days: number | null;
  /** Остаток есть, а расхода за период нет. */
  no_movement: boolean;
}

/** Метрики группы (категории или итога) — в деньгах. */
export interface TurnoverMetrics {
  items_count: number;
  expense_qty: number;
  expense_sum: number;
  /** Ключ документа расхода → сумма (деньги). */
  expense_by_doc: Record<string, number>;
  avg_stock_sum: number;
  stock_sum: number;
  turnover_days: number | null;
  cover_days: number | null;
  no_movement_count: number;
  no_movement_stock_sum: number;
}

export interface TurnoverCategory extends TurnoverMetrics {
  /** Пустая строка — группа «(без категории)». */
  ref: string;
  name: string;
  items: TurnoverItem[];
}

export interface TurnoverOrganization {
  ref: string;
  name: string;
}

export interface TurnoverSummary {
  /** ISO-момент расчёта (UTC, с таймзоной). */
  as_of: string;
  /** Начало и конец периода — время базы 1С, БЕЗ таймзоны. */
  period_from: string;
  period_to: string;
  days: number;
  organization: TurnoverOrganization;
  /** ISO-даты срезов остатка; последний — текущий. */
  snapshot_dates: string[];
  totals: TurnoverMetrics;
  categories: TurnoverCategory[];
  /** Товар у контрагентов (комиссия, переработка) — НЕ склад, в оборачиваемость
   *  не входит; ожидаемое исключение, поэтому отдельным полем, а не в `warnings`.
   *  По убыванию суммы. */
  counterparty_stock: TurnoverCounterparty[];
  counterparty_stock_qty: number;
  counterparty_stock_sum: number;
  /** Исключённый не-складской остаток, минусы, ненайденные имена. */
  warnings: string[];
}

/** Остаток у одного контрагента (`TurnoverCounterpartyOut`). `in_snapshots_only` —
 *  сейчас ноль, был только в прошлых срезах. `name` — GUID, если в справочнике
 *  контрагент без наименования (тогда рядом строка в `warnings`). */
export interface TurnoverCounterparty {
  ref: string;
  name: string;
  stock_qty: number;
  stock_sum: number;
  in_snapshots_only: boolean;
}

/** Периоды, которые принимает бэкенд (`service.ALLOWED_DAYS`); другой — 422. */
export const TURNOVER_DAYS = [30, 90, 180] as const;
export type TurnoverDays = (typeof TURNOVER_DAYS)[number];

/** Подписи документов расхода — ОДНО место (ключи `service.EXPENSE_DOCS`).
 *  Неизвестный ключ показываем как есть — это видно, в отличие от пропажи. */
const DOC_TITLES: Record<string, string> = {
  assembly: "Сборка",
  kitting: "Комплектация",
  sale: "Продажа",
  ptmo: "ПТМО",
};

export function expenseDocTitle(key: string): string {
  return DOC_TITLES[key] ?? key;
}

/** Цвет документа расхода на полоске разбивки и в легенде — рядом с подписями,
 *  одна палитра на экран. Неизвестный ключ — нейтральный серый: в легенде он
 *  всё равно подписан своим ключом, так что не теряется. */
const DOC_COLORS: Record<string, string> = {
  assembly: "bg-sky-500",
  kitting: "bg-violet-400",
  sale: "bg-emerald-500",
  ptmo: "bg-amber-400",
};

export function expenseDocColor(key: string): string {
  return DOC_COLORS[key] ?? "bg-slate-400";
}

export async function getWarehouseTurnoverSummary(
  days: TurnoverDays,
): Promise<TurnoverSummary> {
  const { data } = await api.get<TurnoverSummary>("/warehouse-turnover/summary", {
    params: { days },
  });
  return data;
}
